import _ from 'lodash'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import { RootNavigationInfo } from 'thunderbolt-viewer-manager-types'
import { Router, IRouter } from 'feature-router'
import { createPromise } from '@wix/thunderbolt-commons'
import { NavigationManagerSymbol, INavigationManager } from 'feature-navigation-manager'
import { ILightbox, LightboxSymbol } from 'feature-lightbox'
import { IPageProvider, PageProviderSymbol } from 'feature-pages'
import {
	CarmiInstance,
	EditorFeatureConfigSymbol,
	NavigationInfoSymbol,
	LoggerSymbol,
	ILogger,
	LOADING_PHASES,
	ILayoutDoneService,
	LayoutDoneServiceSymbol,
	ExperimentsSymbol,
} from '@wix/thunderbolt-symbols'
import type {
	EditorNavigationConfig,
	EditorNavigationState,
	IEditorNavigation,
	IIsDuringNavigationInteraction,
	INavigationInfo,
	PartialNavigationInfo,
} from './types'
import { IsDuringNavigationInteractionSymbol, name } from './symbols'

export const EditorNavigation = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		CarmiInstance,
		PageProviderSymbol,
		NavigationInfoSymbol,
		NavigationManagerSymbol,
		Router,
		LightboxSymbol,
		LoggerSymbol,
		LayoutDoneServiceSymbol,
		IsDuringNavigationInteractionSymbol,
		ExperimentsSymbol,
	],
	(
		{ pageIdToPageRoute }: EditorNavigationConfig,
		{ navigation }: DSCarmi,
		pageProvider: IPageProvider,
		navigationInfo: INavigationInfo,
		navigationManager: INavigationManager,
		router: IRouter,
		popups: ILightbox,
		logger: ILogger,
		layoutDoneService: ILayoutDoneService,
		{ isDuringNavigationInteraction }: IIsDuringNavigationInteraction
	): IEditorNavigation => {
		const state: EditorNavigationState = {
			nextNavigationIntent: Promise.resolve(),
			isNavigationToDeletedPage: false,
		}

		const onNavigationStart = (navInfo: Partial<RootNavigationInfo>) => {
			state.isNavigationToDeletedPage = false
			navigationInfo.updateNextRootNavigationInfo(navInfo)
		}

		const onNavigationEnd = (navInfo: Partial<RootNavigationInfo>) => {
			navigationInfo.updateCurrentRootNavigationInfo(navInfo)
		}

		const getDynamicPageUrl = (navInfo: Partial<RootNavigationInfo>) =>
			`./${navInfo.routerDefinition!.prefix}/${navInfo.innerRoute === '/' ? '' : navInfo.innerRoute}`

		const loadMasterPage = async () => {
			await pageProvider('masterPage', 'masterPage')
		}

		const isDynamicPageNavInfo = (navInfo: PartialNavigationInfo) =>
			navInfo.routerDefinition && typeof navInfo.innerRoute === 'string'

		const isValidNavInfo = (navInfo: PartialNavigationInfo) =>
			navInfo && (navInfo.pageId || isDynamicPageNavInfo(navInfo))

		return {
			navigateTo: async (wantedNavInfo, { isSameContext } = { isSameContext: true }) => {
				// Disable navigation from DM to the same route info and same context (viewMode & editorPreviewMode)
				// nextNavInfo most of the cases is null since we are initialized it after this validation. adding currentNavInfo for comparison
				const navInfo =
					navigationInfo.getNextRootNavigationInfo() || navigationInfo.getCurrentRootNavigationInfo()
				if (_.isEqual(navInfo, wantedNavInfo) && isSameContext) {
					return
				}
				if (!isValidNavInfo(wantedNavInfo)) {
					logger.captureError(new Error('corrupted navInfo'), {
						tags: { feature: 'navigation', methodName: 'navigateTo' },
						extra: { wantedNavInfo },
					})
					return
				}

				logger.phaseStarted(LOADING_PHASES.DS_INITIAL_NAVIGATION)

				// When we call loadMasterPage() we load the masterPage and update the structure which then triggers componentWillMount.
				//  However we already call componentWillMount for each page at the end of navigation, so this InitialNavigationPhase
				//  is meant to block that first call.
				navigationInfo.startInitialNavigationPhase()
				onNavigationStart(wantedNavInfo)
				logger.phaseStarted(LOADING_PHASES.DS_LOAD_MASTER_PAGE)
				await loadMasterPage()
				logger.phaseEnded(LOADING_PHASES.DS_LOAD_MASTER_PAGE)

				await popups.close()

				const { promise: renderDonePromise, resolver: resolveRenderDonePromise } = createPromise()

				const renderDoneCallbackId = layoutDoneService.registerToRenderDone(() => resolveRenderDonePromise(), {
					once: true,
				})

				const navigateToUrl = async (url: string) => {
					logger.phaseStarted(LOADING_PHASES.DS_ROUTER_NAVIGATE)
					state.isNavigationToDeletedPage = !url
					const didNavigate = state.isNavigationToDeletedPage ? false : await router.navigate(url)

					navigationInfo.endInitialNavigationPhase()

					if (!didNavigate) {
						navigationManager.endNavigation()
						navigationManager.setShouldBlockRender(false)
						layoutDoneService.unregisterToLayoutDone(renderDoneCallbackId)
						resolveRenderDonePromise()

						if (state.isNavigationToDeletedPage) {
							navigationInfo.updateNextRootNavigationInfo(navigationInfo.getCurrentRootNavigationInfo())
						}
					}
					logger.phaseEnded(LOADING_PHASES.DS_ROUTER_NAVIGATE)
				}

				// @ts-ignore
				const { pageId, tpaInnerRoute, relativeUrl } = wantedNavInfo
				if (pageId) {
					if (popups.isLightbox(pageId)) {
						await popups.open(pageId)
						navigationInfo.endInitialNavigationPhase()
					} else {
						let urlToNavigate = pageIdToPageRoute[pageId]
						if (tpaInnerRoute) {
							urlToNavigate = tpaInnerRoute
						} else if (relativeUrl) {
							urlToNavigate = relativeUrl
						}
						await navigateToUrl(urlToNavigate)
					}
				} else {
					await navigateToUrl(getDynamicPageUrl(wantedNavInfo))
				}

				if (!state.isNavigationToDeletedPage) {
					navigationInfo.startFinalNavigationPhase()
					if (pageId) {
						navigation.setPageCompId(pageId) // can trigger some carmi side effects which rely on pageApi.getRequestedPageId()
					}
					onNavigationEnd(wantedNavInfo)
					navigationInfo.endFinalNavigationPhase()
					await renderDonePromise
				}
				logger.phaseEnded(LOADING_PHASES.DS_INITIAL_NAVIGATION)
			},
			setNextNavigationIntent: (promise: Promise<void>) => {
				state.nextNavigationIntent = promise
			},
			getNextNavigationIntent: () => state.nextNavigationIntent,
			isDuringNavigationInteraction,
		}
	}
)
